/** Tolgee live strings may contain zero-width / bidi format chars that macOS shows as underscores in filenames. */
export function stripInvisibleUnicode(name: string): string {
    return name
        .replace(/[\u00AD\u200B-\u200F\u2028-\u202E\u2060\uFEFF]/g, '');
}

export function sanitizeFileNameBase(name: string): string {
    const cleaned = stripInvisibleUnicode(name)
        .replace(/&shy;|&nbsp;|<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/[\\/:*?"<>|]/g, '-')
        .replace(/_+/g, '-')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '');

    return cleaned || 'export';
}

export function formatTableExportDate(date: Date = new Date()): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}${month}${day}`;
}

export function buildTableExportFileName(baseName: string, date: Date = new Date()): string {
    return `${sanitizeFileNameBase(baseName)}_${formatTableExportDate(date)}`;
}

export function buildReportExportFileNameWithDateRange(
    baseName: string,
    fromDate?: Date,
    toDate?: Date,
): string {
    const label = sanitizeFileNameBase(baseName);
    const from = fromDate ? formatTableExportDate(fromDate) : '';
    const to = toDate ? formatTableExportDate(toDate) : '';

    if (!from && !to) {
        return label;
    }

    const range = from && to ? `${from}-${to}` : (from || to);
    return `${label}_${range}`;
}

/**
 * Collapses legacy/padded names like "Personen____20260826" to "Personen_20260826".
 */
export function normalizeTableExportFileName(name: string): string {
    const cleaned = name
        .replace(/\.xlsx$/i, '')
        .replace(/_+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const rangeMatch = cleaned.match(/^(.+?)\s+(\d{8}-\d{8})$/);
    if (rangeMatch) {
        return `${sanitizeFileNameBase(rangeMatch[1])}_${rangeMatch[2]}`;
    }

    const ymdMatch = cleaned.match(/^(.+?)\s+(\d{8})$/);
    if (ymdMatch) {
        return `${sanitizeFileNameBase(ymdMatch[1])}_${ymdMatch[2]}`;
    }

    const isoMatch = cleaned.match(/^(.+?)\s+(\d{4}-\d{2}-\d{2})$/);
    if (isoMatch) {
        return `${sanitizeFileNameBase(isoMatch[1])}_${isoMatch[2].replace(/-/g, '')}`;
    }

    return sanitizeFileNameBase(cleaned);
}

export function sanitizeExcelWorksheetName(name: string): string {
    const sanitized = stripInvisibleUnicode(name)
        .replace(/[\[\]:*?/\\]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    return sanitized.substring(0, 31) || 'Export';
}
